import React, { ReactNode, memo } from "react";
import { Box, InputLabelProps } from "@mui/material";
import {
  Control,
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";

import AppCombobox, { AppComboboxProps } from "../AppCombobox";
import { AppOptionType } from "@/models";
import AppFromLabelField from "./AppFromLabelField";

const AppFormControlAutocomplete = <T extends FieldValues>({
  label,
  options,
  control,
  name,
  rules,
  isMultiple = false,
  controlProps,
  labelProps,
  actionsButton,
  autocompleteProps,
  onChangeValueForm,
  classNameContainer,
  isLoading,
  helper,
}: AppFormControlAutocompleteProps<T>) => {
  return (
    <AppFromLabelField
      label={label}
      labelProps={{
        ...labelProps,
      }}
      className={classNameContainer}
      required={labelProps?.required}
      helper={helper}
    >
      <Box className="flex flex-row items-center">
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={({
            field: { value, onChange, ref, ...otherFieldProps },
            fieldState: { error: errorField },
          }) => {
            return (
              <AppCombobox
                isLoading={isLoading}
                isOptionEqualToValue={(option, value) => {
                  if (isMultiple) {
                    return option.value === value.value;
                  } else {
                    return option.value === value;
                  }
                }}
                disableCloseOnSelect={isMultiple}
                multiple={isMultiple}
                options={options}
                value={
                  isMultiple
                    ? ((value as any) || [])?.map((valueO: unknown) =>
                        options?.find((item) => item.value === valueO)
                      )
                    : value || null
                }
                ref={ref}
                onChange={(_, newValue) => {
                  let newVal;
                  if (isMultiple) {
                    const newValueMultiple = newValue as AppOptionType[];
                    newVal = (newValueMultiple || []).map(
                      (valueMultiple) => valueMultiple.value
                    );
                  } else {
                    const newValueSingle = newValue as AppOptionType;
                    newVal = newValueSingle?.value;
                  }
                  onChange(newVal);
                  if (onChangeValueForm instanceof Function)
                    onChangeValueForm(newVal);
                }}
                {...autocompleteProps}
                textFieldProps={{
                  // inputRef: ref,
                  error: Boolean(errorField),
                  helperText: <>{errorField?.message}</>,
                  ...autocompleteProps?.textFieldProps,
                  // required: requiredFiled,
                }}
              />
            );
          }}
          {...controlProps}
        />
        {actionsButton}
      </Box>
    </AppFromLabelField>
  );
};

export type AppFormControlAutocompleteProps<T extends FieldValues> = {
  isLoading?: boolean;
  label?: string | ReactNode;
  options: Array<AppOptionType>;
  control: Control<any>;
  name: FieldPath<T>;
  isMultiple?: boolean;
  rules?: RegisterOptions;
  controlProps?: Omit<ControllerProps, "render" | "name" | "control">;
  actionsButton?: ReactNode;
  labelProps?: InputLabelProps;
  autocompleteProps?: Omit<AppComboboxProps, "options">;
  onChangeValueForm?: (
    data?: Array<string | number | null> | string | number | null
  ) => void;
  classNameContainer?: string;
  helper?: string | ReactNode;
};

export default memo(AppFormControlAutocomplete);
